import {Icon} from '@atoms';
import {useModal} from '@hooks';
import {trackEvent} from '@lib/infrastructure';
import {ConnectionUser, useAppSelector} from '@lib/store';
import {Avatar} from '@molecules';
import {cn} from '@utils';
import {Fragment, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {ConnectionLocation, ConnectionOnMapModal} from './ConnectionOnMapModal';
import {ReservationOwner, Workspace} from '@lib/store/src/data/timeslots/apiTypes';
import {timeLabel, isFullDay} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {getOccupantsFromSection, LocationSection} from '@lib/store/src/data/timeslots/viewmodel-helpers';

export const ConnectionOverviewOccupantsSection = ({
  dateKey,
  locationSection,
  groupId,
  asBigPage,
}: {
  dateKey: string;
  locationSection: LocationSection;
  groupId?: string;
  asBigPage?: boolean;
}) => {
  const {t} = useTranslation();

  const {addPages, openModal, closeModal, removePages, setBigPages} = useModal();

  const occupants = getOccupantsFromSection(locationSection, groupId);

  const currentUserId = useAppSelector((state) => state.data.user.user.id);

  const sortedOccupants = useMemo(
    () =>
      occupants.sort((a, b) => {
        // Current user goes first
        if (a.user.id === currentUserId && b.user.id !== currentUserId) return -1;
        if (b.user.id === currentUserId && a.user.id !== currentUserId) return 1;

        // Then alphabetical by name
        return a.user.name.localeCompare(b.user.name);
      }),
    [occupants, currentUserId],
  );

  const showConnectionOnMap = (user: ConnectionUser | ReservationOwner, location: ConnectionLocation) => {
    const mapView = (
      <ConnectionOnMapModal
        user={user}
        workspace={location}
      />
    );

    trackEvent('Home_ConnectionsOverview__ExpandMap');

    addPages([mapView]);
    if (asBigPage) setBigPages([mapView]);
    openModal();
  };

  if (sortedOccupants.length === 0) {
    return null;
  }

  return (
    <div>
      {!groupId && (
        <p className="text-mapiq-black-500">
          {sortedOccupants.length} {t('connection:Connection', {count: sortedOccupants.length})}
        </p>
      )}

      <div className="divide-y">
        {sortedOccupants.map(({user, reservations}) => {
          const workspace = reservations.at(0)?.workspace;

          return (
            <div
              className={cn('grid grid-cols-[auto_1fr_auto] gap-x-2 items-center py-4')}
              key={user.id}>
              <Avatar
                shouldShowCheckIn
                user={user}
              />
              <p className="truncate">
                {user.name} {currentUserId === user.id && <span className="text-mapiq-black-500">({t('You')})</span>}
              </p>
              <div className="grid grid-cols-subgrid col-span-full">
                {reservations.map((r) => {
                  const loc = getWorkspaceLabel(r.workspace);
                  const time = isFullDay(r.localStart, r.localEnd) ? null : timeLabel(r.localStart, r.localEnd, t);
                  const hasWorkspaceLocation = !!workspace && 'areaName' in workspace && !!workspace.areaName;

                  return (
                    <Fragment key={r.id}>
                      <span className="col-start-2 py-2">
                        <span className="block">{loc}</span>
                        <span className="block">{time && `(${time})`}</span>
                      </span>

                      {hasWorkspaceLocation ? (
                        <button
                          className={'flex-none'}
                          aria-label={t('ShowOnMap')}
                          onClick={() => {
                            showConnectionOnMap(user, workspace);
                          }}>
                          <Icon
                            icon="map"
                            color="currentColor"
                            className="size-5 text-mapiq-black-500"
                          />
                        </button>
                      ) : (
                        <span className="w-5" />
                      )}
                      <hr className="col-span-1 col-start-2 last-of-type:hidden" />
                    </Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const getWorkspaceLabel = (workspace?: Workspace) => {
  if (!workspace) return '';

  if ('areaName' in workspace && workspace.areaName) {
    return `${workspace.floorName ?? ''}${workspace.floorName && workspace.areaName ? ', ' : ''}${workspace.areaName}`;
  }

  if ('floorName' in workspace && workspace.floorName) {
    return workspace.floorName;
  }

  return '';
};
